import { Data, Model } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../../DomainTypes';
import Pipeline from '../../Models/Catalogue/Pipeline/Pipeline';
import CommissionType from '../../Models/Catalogue/Pipeline/CommissionType';
import Rate from '../../Models/Catalogue/Pipeline/Rate';
import LadderAge from '../../Models/Catalogue/Pipeline/LadderAge';
import LadderProjectionPagedCriteria from '../../Models/Catalogue/Pipeline/Lookups/LadderProjectionPagedCriteria';
import LadderProjection from '../../Models/Catalogue/Pipeline/LadderProjection';
import LadderProjectionDividendCriteria from '../../Models/Catalogue/Pipeline/Lookups/LadderProjectionDividendCriteria';
import LadderProjectionDividendLookup from '../../Models/Catalogue/Pipeline/Lookups/LadderProjectionDividendLookup';
import PRPRatePagedLookupCriteria from '../../Models/Catalogue/Pipeline/Lookups/PRPRatePagedLookupCriteria';
import PRPRate from '../../Models/Catalogue/Pipeline/PRPRate';
import IDTRatePagedLookupCriteria from '../../Models/Catalogue/Pipeline/Lookups/IDTRatePagedLookupCriteria';
import IDTRate from '../../Models/Catalogue/Pipeline/IDTRate';
import CostRecoveryPremium from '../../Models/Catalogue/Pipeline/CostRecoveryPremium';
import BankAccountType from '../../Models/Catalogue/Pipeline/BankAccountType';
import CommissionTypeLookup from '../../Models/Catalogue/Pipeline/Lookups/CommissionTypeLookup';

export interface IPipelineApiClient {

    getPipelines(): AxiosPromise<Array<Model.PlainTrackedObject<Pipeline>>>;

    savePipelines(pipelines: Array<Model.PartialPlainObject<Pipeline>>): AxiosPromise<Array<Model.PlainTrackedObject<Pipeline>>>;

    getCommissionTypes(): AxiosPromise<Array<Model.PlainTrackedObject<CommissionType>>>;

    saveCommissionTypes(commissionTypes: Array<Model.PartialPlainObject<CommissionType>>): AxiosPromise<Array<Model.PlainTrackedObject<CommissionType>>>;

    getRates(): AxiosPromise<Array<Model.PlainTrackedObject<Rate>>>;

    saveRates(rates: Array<Model.PartialPlainObject<Rate>>): AxiosPromise<Array<Model.PlainTrackedObject<Rate>>>;

    getLadderAges(): AxiosPromise<Array<Model.PlainTrackedObject<LadderAge>>>;

    saveLadderAges(ladderAges: Array<Model.PartialPlainObject<LadderAge>>): AxiosPromise<Array<Model.PlainTrackedObject<LadderAge>>>;

    getLadderProjectionsPaged(request: Model.PartialPlainObject<Data.PageRequest<LadderProjectionPagedCriteria>>): AxiosPromise<Data.PageResult<Model.PlainTrackedObject<LadderProjection>>>;

    saveLadderProjections(ladderProjections: Array<Model.PartialPlainObject<LadderProjection>>): AxiosPromise<Array<Model.PlainTrackedObject<LadderProjection>>>;

    getLadderProjectionDividends(criteria: Model.PartialPlainObject<LadderProjectionDividendCriteria>): AxiosPromise<Array<Model.PlainObject<LadderProjectionDividendLookup>>>;

    getPRPRateLookupsPaged(prpRequest: Model.PartialPlainObject<Data.PageRequest<PRPRatePagedLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainTrackedObject<PRPRate>>>;

    savePRPRates(prpRates: Array<Model.PartialPlainObject<PRPRate>>): AxiosPromise<Array<Model.PlainTrackedObject<PRPRate>>>;

    getIDTRateLookupsPaged(idtRequest: Model.PartialPlainObject<Data.PageRequest<IDTRatePagedLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainTrackedObject<IDTRate>>>;

    saveIDTRates(idtRates: Array<Model.PartialPlainObject<IDTRate>>): AxiosPromise<Array<Model.PlainTrackedObject<IDTRate>>>;

    getCostRecoveryPremiums(): AxiosPromise<Array<Model.PlainTrackedObject<CostRecoveryPremium>>>;

    saveCostRecoveryPremiums(costRecoveryPremiums: Array<Model.PartialPlainObject<CostRecoveryPremium>>): AxiosPromise<Array<Model.PlainTrackedObject<CostRecoveryPremium>>>;

    getBankAccountTypes(): AxiosPromise<Array<Model.PlainTrackedObject<BankAccountType>>>;

    saveBankAccountTypes(bankAccountTypes: Array<Model.PartialPlainObject<BankAccountType>>): AxiosPromise<Array<Model.PlainTrackedObject<BankAccountType>>>;

    /** 
     * Gets a list of commission type lookups
     */
    getCommissionTypeLookups(): AxiosPromise<Array<Model.PlainObject<CommissionTypeLookup>>>;

    // Client only properties / methods
}

@injectable()
export default class PipelineApiClient extends Data.ApiClientBase implements IPipelineApiClient {

    constructor (config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/catalogue/pipelines`);
    }

    public getPipelines(): AxiosPromise<Array<Model.PlainTrackedObject<Pipeline>>> {
        return this.axios.get(`${this.apiPath}/pipeline-dates`);
    }

    public savePipelines(pipelines: Array<Model.PartialPlainObject<Pipeline>>): AxiosPromise<Array<Model.PlainTrackedObject<Pipeline>>> {
        return this.axios.post(`${this.apiPath}/pipeline-dates`, pipelines);
    }

    public getCommissionTypes(): AxiosPromise<Array<Model.PlainTrackedObject<CommissionType>>> {
        return this.axios.get(`${this.apiPath}/commission-types`);
    }

    public saveCommissionTypes(commissionTypes: Array<Model.PartialPlainObject<CommissionType>>): AxiosPromise<Array<Model.PlainTrackedObject<CommissionType>>> {
        return this.axios.post(`${this.apiPath}/commission-types`, commissionTypes);
    }

    public getRates(): AxiosPromise<Array<Model.PlainTrackedObject<Rate>>> {
        return this.axios.get(`${this.apiPath}/rates`);
    }

    public saveRates(rates: Array<Model.PartialPlainObject<Rate>>): AxiosPromise<Array<Model.PlainTrackedObject<Rate>>> {
        return this.axios.post(`${this.apiPath}/rates`, rates);
    }

    public getLadderAges(): AxiosPromise<Array<Model.PlainTrackedObject<LadderAge>>> {
        return this.axios.get(`${this.apiPath}/ladder-ages`);
    }

    public saveLadderAges(ladderAges: Array<Model.PartialPlainObject<LadderAge>>): AxiosPromise<Array<Model.PlainTrackedObject<LadderAge>>> {
        return this.axios.post(`${this.apiPath}/ladder-ages`, ladderAges);
    }

    public getLadderProjectionsPaged(request: Model.PartialPlainObject<Data.PageRequest<LadderProjectionPagedCriteria>>): AxiosPromise<Data.PageResult<Model.PlainTrackedObject<LadderProjection>>> {
        return this.axios.post(`${this.apiPath}/ladder-projections-paged`, request);
    }

    public saveLadderProjections(ladderProjections: Array<Model.PartialPlainObject<LadderProjection>>): AxiosPromise<Array<Model.PlainTrackedObject<LadderProjection>>> {
        return this.axios.post(`${this.apiPath}/ladder-projections`, ladderProjections);
    }

    public getLadderProjectionDividends(criteria: Model.PartialPlainObject<LadderProjectionDividendCriteria>): AxiosPromise<Array<Model.PlainObject<LadderProjectionDividendLookup>>> {
        return this.axios.post(`${this.apiPath}/ladder-projection-dividends`, criteria);
    }

    public getPRPRateLookupsPaged(prpRequest: Model.PartialPlainObject<Data.PageRequest<PRPRatePagedLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainTrackedObject<PRPRate>>> {
        return this.axios.post(`${this.apiPath}/prp-rate-paged`, prpRequest);
    }

    public savePRPRates(prpRates: Array<Model.PartialPlainObject<PRPRate>>): AxiosPromise<Array<Model.PlainTrackedObject<PRPRate>>> {
        return this.axios.post(`${this.apiPath}/prp-rates`, prpRates);
    }

    public getIDTRateLookupsPaged(idtRequest: Model.PartialPlainObject<Data.PageRequest<IDTRatePagedLookupCriteria>>): AxiosPromise<Data.PageResult<Model.PlainTrackedObject<IDTRate>>> {
        return this.axios.post(`${this.apiPath}/idtr-paged`, idtRequest);
    }

    public saveIDTRates(idtRates: Array<Model.PartialPlainObject<IDTRate>>): AxiosPromise<Array<Model.PlainTrackedObject<IDTRate>>> {
        return this.axios.post(`${this.apiPath}/idt-rates`, idtRates);
    }

    public getCostRecoveryPremiums(): AxiosPromise<Array<Model.PlainTrackedObject<CostRecoveryPremium>>> {
        return this.axios.get(`${this.apiPath}/cost-recovery-premiums`);
    }

    public saveCostRecoveryPremiums(costRecoveryPremiums: Array<Model.PartialPlainObject<CostRecoveryPremium>>): AxiosPromise<Array<Model.PlainTrackedObject<CostRecoveryPremium>>> {
        return this.axios.post(`${this.apiPath}/cost-recovery-premiums`, costRecoveryPremiums);
    }

    public getBankAccountTypes(): AxiosPromise<Array<Model.PlainTrackedObject<BankAccountType>>> {
        return this.axios.get(`${this.apiPath}/bank-account-types`);
    }

    public saveBankAccountTypes(bankAccountTypes: Array<Model.PartialPlainObject<BankAccountType>>): AxiosPromise<Array<Model.PlainTrackedObject<BankAccountType>>> {
        return this.axios.post(`${this.apiPath}/bank-account-types`, bankAccountTypes);
    }

    public getCommissionTypeLookups(): AxiosPromise<Array<Model.PlainObject<CommissionTypeLookup>>> {
        return this.axios.get(`${this.apiPath}/lookups`);
    }

    // Client only properties / methods
}